import React, { useState } from "react";
import Profile from "./Profile";
import General from "./General";
import Support from "./Support";
import FAQ from "./FAQ";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Switch,
  Button,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import StorageIcon from '@mui/icons-material/Storage';
import HelpIcon from '@mui/icons-material/Help';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

const categories = [
  { key: 'general', label: 'General', icon: <SettingsIcon /> },
  { key: 'notifications', label: 'Notifications', icon: <NotificationsIcon /> },
  { key: 'data', label: 'Data', icon: <StorageIcon /> },
  { key: 'security', label: 'Security', icon: <SecurityIcon /> },
  { key: 'account', label: 'Account', icon: <AccountCircleIcon /> },
  { key: 'support', label: 'Support', icon: <HelpIcon /> },
  { key: 'faq', label: 'FAQs', icon: <QuestionAnswerIcon /> },
];

const Settings = ({ initialTab }) => {
  const [selected, setSelected] = useState(initialTab || 'general');
  const isMobile = useMediaQuery('(max-width:600px)');

  // Mobile: use tabs across the top; Desktop: sidebar + panel
  if (isMobile) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '92vw',
        maxWidth: 560,
        mx: 'auto',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
      }}>
        <Tabs
          value={selected}
          onChange={(e, val) => setSelected(val)}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          aria-label="Settings tabs"
          sx={{ 
            bgcolor: '#e1ecc8',
            '& .MuiTab-root': {
              color: '#516d4f',
            },
            '& .MuiTab-root.Mui-selected': {
              color: '#516d4f',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#a0c49d',
              height: 3,
            },
          }}
        >
          {categories.map(cat => (
            <Tab
              key={cat.key}
              value={cat.key}
              icon={cat.icon}
              iconPosition="start"
              label={cat.label}
              sx={{ textTransform: 'none', minHeight: 48, minWidth: 'auto', px: 1.5 }}
            />
          ))}
        </Tabs>
        <Box sx={{ p: 2, bgcolor: '#ffff', minHeight: 360 }}>
          {selected === 'general' && <General />}
          {selected === 'data' && (
            <>
              <Typography variant="h6" sx={{ mb: 2, color: '#516d4f', fontWeight: 600 }}>Data controls</Typography>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#516d4f', fontSize: 14 }}>Improve the model for everyone</Typography>
                <Switch defaultChecked sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#a0c49d',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#a0c49d',
                  },
                }} />
              </Box>
              <Divider sx={{ mb: 2, borderColor: '#e1ecc8' }} />
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#516d4f', fontSize: 14 }}>Shared links</Typography>
                <Button variant="outlined" size="small" sx={{
                  borderColor: '#a0c49d',
                  color: '#516d4f',
                  '&:hover': {
                    borderColor: '#8ab287',
                    backgroundColor: '#f7fbe8',
                  },
                }}>Manage</Button>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#516d4f', fontSize: 14 }}>Archived chats</Typography>
                <Button variant="outlined" size="small" sx={{
                  borderColor: '#a0c49d',
                  color: '#516d4f',
                  '&:hover': {
                    borderColor: '#8ab287',
                    backgroundColor: '#f7fbe8',
                  },
                }}>Manage</Button>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#516d4f', fontSize: 14 }}>Archive all chats</Typography>
                <Button variant="contained" size="small" sx={{
                  backgroundColor: '#a0c49d',
                  color: '#fbffe8',
                  '&:hover': {
                    backgroundColor: '#8ab287',
                  },
                }}>Archive all</Button>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#516d4f', fontSize: 14 }}>Delete all chats</Typography>
                <Button variant="contained" size="small" sx={{
                  backgroundColor: '#d32f2f',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                }}>Delete all</Button>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: '#516d4f', fontSize: 14 }}>Export data</Typography>
                <Button variant="outlined" size="small" sx={{
                  borderColor: '#a0c49d',
                  color: '#516d4f',
                  '&:hover': {
                    borderColor: '#8ab287',
                    backgroundColor: '#f7fbe8',
                  },
                }}>Export</Button>
              </Box>
            </>
          )}
          {selected === 'account' && <Profile />}
          {selected === 'support' && <Support />}
          {selected === 'faq' && <FAQ />}
          {selected !== 'general' && selected !== 'data' && selected !== 'account' && selected !== 'support' && selected !== 'faq' && (
            <Typography variant="body1" color="text.secondary">{categories.find(c => c.key === selected)?.label} settings coming soon...</Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Desktop layout (unchanged visually)
  return (
    <Box sx={{ display: 'flex', minHeight: 480, borderRadius: 3, overflow: 'hidden', width: 620, maxWidth: '90vw' }}>
      {/* Sidebar */}
      <Box sx={{ width: 200, borderRight: 1, borderColor: '#e0e0e0', bgcolor: '#e1ecc8', p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'stretch' }}>
        <List>
          {categories.map(cat => (
            <ListItemButton
              key={cat.key}
              selected={selected === cat.key}
              onClick={() => setSelected(cat.key)}
              sx={{ 
                borderRadius: 2, 
                mb: 0.5,
                color: '#516d4f',
                '&.Mui-selected': {
                  backgroundColor: '#a0c49d',
                  color: '#fbffe8',
                  '&:hover': {
                    backgroundColor: '#8ab287',
                  },
                },
                '&:hover': {
                  backgroundColor: '#c8e1b5',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: selected === cat.key ? '#fbffe8' : '#516d4f' }}>{cat.icon}</ListItemIcon>
              <ListItemText primary={cat.label} />
            </ListItemButton>
          ))}
        </List>
      </Box>
      {/* Main Panel */}
      <Box sx={{ width: 420, px: 3, pt: 2, pb: 2, bgcolor: '#ffff', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: 480, height: 480, overflowY: 'auto', overflowX: 'hidden' }}>
        {selected === 'general' && <General />}
        {selected === 'data' && (
          <>
            <Typography variant="h6" sx={{ mb: 2, color: '#516d4f', fontWeight: 600 }}>Data controls</Typography>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{ flex: 1, color: '#516d4f' }}>Improve the model for everyone</Typography>
              <Switch defaultChecked sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#a0c49d',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#a0c49d',
                },
              }} />
            </Box>
            <Divider sx={{ mb: 2, borderColor: '#e1ecc8' }} />
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{ flex: 1, color: '#516d4f' }}>Shared links</Typography>
              <Button variant="outlined" size="small" sx={{
                borderColor: '#a0c49d',
                color: '#516d4f',
                '&:hover': {
                  borderColor: '#8ab287',
                  backgroundColor: '#f7fbe8',
                },
              }}>Manage</Button>
            </Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{ flex: 1, color: '#516d4f' }}>Archived chats</Typography>
              <Button variant="outlined" size="small" sx={{
                borderColor: '#a0c49d',
                color: '#516d4f',
                '&:hover': {
                  borderColor: '#8ab287',
                  backgroundColor: '#f7fbe8',
                },
              }}>Manage</Button>
            </Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{ flex: 1, color: '#516d4f' }}>Archive all chats</Typography>
              <Button variant="contained" size="small" sx={{
                backgroundColor: '#a0c49d',
                color: '#fbffe8',
                '&:hover': {
                  backgroundColor: '#8ab287',
                },
              }}>Archive all</Button>
            </Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{ flex: 1, color: '#516d4f' }}>Delete all chats</Typography>
              <Button variant="contained" size="small" sx={{
                backgroundColor: '#d32f2f',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                },
              }}>Delete all</Button>
            </Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography sx={{ flex: 1, color: '#516d4f' }}>Export data</Typography>
              <Button variant="outlined" size="small" sx={{
                borderColor: '#a0c49d',
                color: '#516d4f',
                '&:hover': {
                  borderColor: '#8ab287',
                  backgroundColor: '#f7fbe8',
                },
              }}>Export</Button>
            </Box>
          </>
        )}
        {selected === 'account' && <Profile />}
        {selected === 'support' && <Support />}
        {selected === 'faq' && <FAQ />}
        {selected !== 'general' && selected !== 'data' && selected !== 'account' && selected !== 'support' && selected !== 'faq' && (
          <Typography variant="body1" color="text.secondary">{categories.find(c => c.key === selected)?.label} settings coming soon...</Typography>
        )}
      </Box>
    </Box>
  );
};

export default Settings;
